import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { UserConfigInputs } from '../model/user-config.types';
import UserAdjustConfigFields from './UserAdjustConfigFields';
import ItemListColumnConfigFields from './ItemListColumnConfigFields';

type UpdateConfig = (
    payload: Partial<UserConfigInputs>,
    setErrorMessage: (message: string | null) => void,
    setProcessMessage: (message: string | null) => void,
    setSuccessMessage: (message: string | null) => void,
) => Promise<void>;

interface ConfigTabProps {
    userConfig: UserConfigInputs;
    updateConfig: UpdateConfig;
}

const ConfigMessages = ({ error, process, success }: {
    error: string | null;
    process: string | null;
    success: string | null;
}) => <>
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {process && <div className="alert alert-info" role="alert">{process}</div>}
    {success && <div className="alert alert-success" role="alert">{success}</div>}
</>;

export const AdjustConfigTab = ({ userConfig, updateConfig }: ConfigTabProps) => {
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
    const methods = useForm<UserConfigInputs>({ defaultValues: userConfig });
    const { handleSubmit, reset } = methods;

    React.useEffect(() => { reset(userConfig); }, [userConfig, reset]);

    const onSubmit = async (data: UserConfigInputs) => {
        setErrorMessage(null);
        await updateConfig({
            adjust_no_exposed: data.adjust_no_exposed,
            skip_low_coupon: data.skip_low_coupon,
            adjust_max_step: data.adjust_max_step,
            reset_max_step: data.reset_max_step,
            reset_on_ll: data.reset_on_ll,
            reset_on_ll_countime: data.reset_on_ll_countime,
            should_recover_price_on_active_schedule: data.should_recover_price_on_active_schedule,
        }, setErrorMessage, setProcessMessage, setSuccessMessage);
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <UserAdjustConfigFields />
                <ConfigMessages error={errorMessage} process={processMessage} success={successMessage} />
                <div className="mt-2 d-flex gap-2">
                    <button type="submit" className="btn btn-primary">저장</button>
                </div>
            </form>
        </FormProvider>
    );
};

export const ItemListColumnConfigTab = ({ userConfig, updateConfig }: ConfigTabProps) => {
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
    const methods = useForm<UserConfigInputs>({ defaultValues: userConfig });
    const { handleSubmit, reset, getValues } = methods;

    React.useEffect(() => { reset(userConfig); }, [userConfig, reset]);

    const onSubmit = async () => {
        setErrorMessage(null);
        await updateConfig(
            { item_list_column_config: getValues().item_list_column_config },
            setErrorMessage,
            setProcessMessage,
            setSuccessMessage,
        );
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <ItemListColumnConfigFields />
                <ConfigMessages error={errorMessage} process={processMessage} success={successMessage} />
                <div className="mt-2 d-flex gap-2">
                    <button type="submit" className="btn btn-primary">저장</button>
                </div>
            </form>
        </FormProvider>
    );
};
